/**
 * Reject-task step — optional reason, then deny via FloatingFooter CTA.
 * White Task Edit surface; destructive intent via danger-tone CTA.
 */
import { useState, useCallback } from 'react';
import { updateTaskStatusFromWeb } from '../services/api';
import FloatingFooter, {
  FloatingFooterCta,
  FloatingFooterNavButton,
} from '../components/FloatingFooter';

interface Props {
  shortId: string;
  onDone: () => void;
  onCancel: () => void;
}

const MAX_REASON_LENGTH = 500;

export default function DenyStep({ shortId, onDone, onCancel }: Props) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await updateTaskStatusFromWeb({
        shortId,
        newStatus: 'denied',
        comment: comment.trim() || undefined,
      });
      if (!res.ok) {
        setError(res.error || 'Could not reject this task.');
        setSubmitting(false);
        return;
      }
      onDone();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Deny failed:', err);
      setError('Could not reject this task. Check your connection and try again.');
      setSubmitting(false);
    }
  }, [shortId, comment, onDone]);

  return (
    <div className="page-shell">
      <header className="page-header !pb-1">
        <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-x9-marineSoft">
          Reject task
        </span>
      </header>

      <div className="page-body">
        <h1 className="text-[28px] font-bold tracking-tight text-x9-ink">
          Reject this task?
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-x9-marineSoft">
          Let the creator know why you can&rsquo;t do this task. They&rsquo;ll review
          your rejection before the link becomes available again.
        </p>

        <div className="mt-6">
          <label
            htmlFor="reason"
            className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-x9-marineSoft"
          >
            Reason (optional)
          </label>
          <textarea
            id="reason"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. I'm out of town this week"
            rows={5}
            maxLength={MAX_REASON_LENGTH}
            autoFocus
            className="field-input-paper resize-none"
          />
          <p className="mt-1 text-right text-xs text-x9-marineSoft/70">
            {comment.length}/{MAX_REASON_LENGTH}
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-[15px] border border-x9-error/30 bg-x9-error/5 p-4 text-sm text-x9-error">
            {error}
          </div>
        )}
      </div>

      <FloatingFooter>
        <FloatingFooterNavButton label="Back" onClick={onCancel} disabled={submitting} />
        <FloatingFooterCta
          tone="danger"
          onClick={handleSubmit}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? 'Rejecting…' : 'Reject task'}
        </FloatingFooterCta>
      </FloatingFooter>
    </div>
  );
}
